import { Component, Texture, Material } from '@wonderlandengine/api';
import { setFirstMaterialTexture, resizeToRatio } from './utils/utils.js';
import { property } from '@wonderlandengine/api/decorators.js';
import { SharedMediaController, SharedMediaPreviewSource } from './media/shared-media-controller.js';
import Logger from './utils/logger.js';

const MODULE = 'SharedMediaVideo';

/**
 * Renders the locally selected / room-wide shared media onto this object's material.
 *
 * This component is a pure renderer: capture, broadcasting and URL synchronization all
 * live in the {@link SharedMediaController} singleton. That split is what allows several
 * preview surfaces (web menu preview, VR menu preview, ...) to coexist in one scene.
 */
export class SharedMediaVideoTexture extends Component {
    static TypeName = 'sharedmedia-video-texture';

    /** Whether to share the camera. If set to false, this will share portions of the desktop */
    /** This property is ignored for now. */
    @property.bool()
    shareCamera!: string;
    /** Material to apply the video texture to */
    @property.material({ required: true })
    material!: Material;
    /** Whether to mute sound */
    @property.bool(true)
    muted!: boolean;
    /** Name of the texture property to set */
    @property.string('auto')
    textureProperty!: string;

    @property.float(91.6)
    canvasMaxScaleWidth: number = 91.6;
    @property.float(52.2)
    canvasMaxScaleHeight: number = 52.2;

    /** Video constraints applied to local screen/camera capture */
    @property.int(1280)
    videoWidthIdeal: number = 1280;
    @property.int(1920)
    videoWidthMax: number = 1920;
    @property.int(720)
    videoHeightIdeal: number = 720;
    @property.int(1080)
    videoHeightMax: number = 1080;
    @property.int(24)
    videoFrameRateIdeal: number = 24;
    @property.int(30)
    videoFrameRateMax: number = 30;

    loaded = false;
    frameUpdateRequested = true;

    video?: HTMLVideoElement;
    texture?: Texture;

    private controller!: SharedMediaController;
    private videoSizeChangeTimeout: number | undefined;
    private streamStartTime: number = 0;
    private lastAppliedWidth: number = 0;
    private lastAppliedHeight: number = 0;
    private firstResizeApplied: boolean = false;
    private initialResizeTimeout: number | undefined;
    private finalResizeTimeout: number | undefined;
    private readonly onPreviewSourceChangedHandler = this.onPreviewSourceChanged.bind(this);

    start() {
        this.controller = SharedMediaController.instance;
        this.controller.setCaptureConstraints({
            videoWidthIdeal: this.videoWidthIdeal,
            videoWidthMax: this.videoWidthMax,
            videoHeightIdeal: this.videoHeightIdeal,
            videoHeightMax: this.videoHeightMax,
            videoFrameRateIdeal: this.videoFrameRateIdeal,
            videoFrameRateMax: this.videoFrameRateMax,
        });
        this.controller.attach();
        this.controller.onPreviewSourceChanged.add(this.onPreviewSourceChangedHandler);

        // A source may already be active when this preview starts (VR menu previews start late).
        this.onPreviewSourceChanged(this.controller.currentPreviewSource);
    }

    private onPreviewSourceChanged(source: SharedMediaPreviewSource | null) {
        this.resetTextureState();

        if (!source) {
            this.video = undefined;
            return;
        }

        this.video = this.controller.previewVideo ?? undefined;
        if (!this.video) {
            Logger.warn(MODULE, 'Preview source changed but the controller has no video element');
            return;
        }

        this.video.addEventListener('playing', this.onVideoPlaying);
        this.video.addEventListener('resize', this.videoSizeChanged, false);

        this.applyPreviewMaterialTint();
        this.frameUpdateRequested = true;

        if (!this.video.paused && this.video.readyState >= this.video.HAVE_CURRENT_DATA) {
            this.loaded = true;
        }

        this.initialResizeTimeout = window.setTimeout(() => this.applyVideoResize(), 800);
        this.finalResizeTimeout = window.setTimeout(() => this.applyVideoResize(), 2000);
    }

    private onVideoPlaying = () => {
        this.loaded = true;
        this.frameUpdateRequested = true;
    };

    private resetTextureState() {
        this.detachVideoListeners();
        this.loaded = false;
        this.texture?.destroy();
        delete this.texture;
        this.lastAppliedWidth = 0;
        this.lastAppliedHeight = 0;
        this.firstResizeApplied = false;
        this.streamStartTime = Date.now();

        if (this.initialResizeTimeout !== undefined) {
            clearTimeout(this.initialResizeTimeout);
            this.initialResizeTimeout = undefined;
        }
        if (this.finalResizeTimeout !== undefined) {
            clearTimeout(this.finalResizeTimeout);
            this.finalResizeTimeout = undefined;
        }
        if (this.videoSizeChangeTimeout !== undefined) {
            clearTimeout(this.videoSizeChangeTimeout);
            this.videoSizeChangeTimeout = undefined;
        }
    }

    private detachVideoListeners() {
        if (!this.video) {
            return;
        }
        this.video.removeEventListener('playing', this.onVideoPlaying);
        this.video.removeEventListener('resize', this.videoSizeChanged);
    }

    private applyPreviewMaterialTint(): void {
        const materialCandidate = this.material as unknown as { setDiffuseColor?: (value: number[]) => void } | undefined;
        if (materialCandidate && typeof materialCandidate.setDiffuseColor === 'function') {
            materialCandidate.setDiffuseColor([0.75, 0.75, 0.75, 1]);
        }
    }

    onDestroy() {
        this.controller?.onPreviewSourceChanged.remove(this.onPreviewSourceChangedHandler);
        this.resetTextureState();
        this.video = undefined;
    }

    applyTexture() {
        // Safety check: ensure video has valid dimensions before creating texture
        if (!this.video || this.video.videoWidth === 0 || this.video.videoHeight === 0) {
            Logger.debug(MODULE, `Cannot apply texture: video not ready (dimensions: ${this.video?.videoWidth || 0}x${this.video?.videoHeight || 0})`);
            return;
        }

        const video = this.video;
        const mat = this.material;
        const pipeline = mat.pipeline;
        const texture = (this.texture = this.engine.textures.create(video));

        if (!setFirstMaterialTexture(mat, texture, this.textureProperty)) {
            Logger.error(MODULE, 'Pipeline ' + pipeline + ' not supported by video-texture');
        }

        if (typeof (video as any).requestVideoFrameCallback === 'function') {
            (video as any).requestVideoFrameCallback(this.updateVideo);
        } else {
            video.addEventListener('timeupdate', () => {
                this.frameUpdateRequested = true;
            });
        }
    }

    update(dt: number) {
        if (this.loaded && this.frameUpdateRequested) {
            if (this.texture) {
                this.texture.update();
            } else {
                /* Apply texture on first frame update request */
                this.applyTexture();
            }
            // Finalize aspect ratio once the first real frame is available
            if (!this.firstResizeApplied && this.video && this.video.videoWidth > 0 && this.video.videoHeight > 0) {
                resizeToRatio(this.video.videoWidth, this.video.videoHeight, this.canvasMaxScaleWidth, this.canvasMaxScaleHeight, this.object);
                this.lastAppliedWidth = this.video.videoWidth;
                this.lastAppliedHeight = this.video.videoHeight;
                this.firstResizeApplied = true;
            }
            this.frameUpdateRequested = false;
        }
    }

    updateVideo = () => {
        this.frameUpdateRequested = true;
        if (this.video && 'requestVideoFrameCallback' in this.video) {
            this.video.requestVideoFrameCallback(this.updateVideo);
        }
    };

    videoSizeChanged = () => {
        // Debounce rapid resize events so orientation changes do not thrash the texture
        if (this.videoSizeChangeTimeout !== undefined) {
            clearTimeout(this.videoSizeChangeTimeout);
        }

        this.videoSizeChangeTimeout = window.setTimeout(() => {
            this.applyVideoResize();
            this.videoSizeChangeTimeout = undefined;
        }, 100);
    };

    private applyVideoResize() {
        if (this.video && this.video.videoWidth > 0 && this.video.videoHeight > 0) {
            const now = Date.now();
            const timeSinceStreamStart = now - this.streamStartTime;

            // Force one resize at >=500ms even if dimensions haven't changed yet
            if (!this.firstResizeApplied && timeSinceStreamStart >= 500) {
                resizeToRatio(this.video.videoWidth, this.video.videoHeight, this.canvasMaxScaleWidth, this.canvasMaxScaleHeight, this.object);
                this.lastAppliedWidth = this.video.videoWidth;
                this.lastAppliedHeight = this.video.videoHeight;
                this.firstResizeApplied = true;
                return;
            }

            // Only apply resize if dimensions actually changed
            if (this.video.videoWidth !== this.lastAppliedWidth || this.video.videoHeight !== this.lastAppliedHeight) {
                Logger.debug(MODULE, `Video size changed from ${this.lastAppliedWidth}x${this.lastAppliedHeight} to ${this.video.videoWidth}x${this.video.videoHeight}`);

                // After stabilization period (1s), recreate texture on ANY dimension change
                if (this.texture && timeSinceStreamStart > 1000) {
                    Logger.debug(MODULE, 'Video dimensions changed after stabilization, recreating texture');
                    this.texture.destroy();
                    this.texture = this.engine.textures.create(this.video);
                    setFirstMaterialTexture(this.material, this.texture, this.textureProperty);
                }

                this.lastAppliedWidth = this.video.videoWidth;
                this.lastAppliedHeight = this.video.videoHeight;

                // Defer resizing until at least 500ms to avoid early flashes
                if (timeSinceStreamStart >= 500) {
                    resizeToRatio(this.video.videoWidth, this.video.videoHeight, this.canvasMaxScaleWidth, this.canvasMaxScaleHeight, this.object);
                    this.firstResizeApplied = true;
                }
            }
        }
    }
}
